import { useCallback, useState } from "react";
import { MAX_EXPORT_ITEMS } from "@strat/format";
import type { ExportRequest } from "@strat/format";
import { dataService } from "../services/api";
import type { RunListParams, StrategyListParams } from "../services/api";
import { downloadDocument } from "./file";
import type { SaveOptions } from "./file";

/** The server's own page cap — ids have to be collected a page at a time. */
const PAGE = 200;

/**
 * Downloading an export, with the request state a button needs.
 *
 * The file is always built by the server and saved as it came back — gzipped
 * in the browser when asked, which changes the bytes on disk but not the
 * document inside them.
 */
export function useExport() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportItems = useCallback(
    async (request: ExportRequest, options: SaveOptions = {}): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        await downloadDocument(await dataService.exportItems(request), options);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  return { exportItems, busy, error, clearError: () => setError(null) };
}

/**
 * Ids for everything matching a filter, up to the export cap.
 *
 * "Export all" means the filtered set, not the visible page — the filters are
 * how you say what you want, and it would be a strange tool that exported only
 * whichever twenty-five rows happened to be on screen.
 */
export async function filteredStrategyIds(params: StrategyListParams): Promise<string[]> {
  const ids: string[] = [];
  for (let offset = 0; ids.length < MAX_EXPORT_ITEMS; offset += PAGE) {
    const page = await dataService.listStrategies({ ...params, limit: PAGE, offset });
    ids.push(...page.items.map((item) => item.id));
    if (ids.length >= page.total || page.items.length === 0) break;
  }
  return ids.slice(0, MAX_EXPORT_ITEMS);
}

export async function filteredRunIds(params: RunListParams): Promise<string[]> {
  const ids: string[] = [];
  for (let offset = 0; ids.length < MAX_EXPORT_ITEMS; offset += PAGE) {
    const page = await dataService.listRuns({ ...params, limit: PAGE, offset });
    ids.push(...page.items.map((item) => item.id));
    if (ids.length >= page.total || page.items.length === 0) break;
  }
  return ids.slice(0, MAX_EXPORT_ITEMS);
}
